import Highcharts from "highcharts";
import Accessibility from "highcharts/modules/accessibility";
import HighchartsReact from "highcharts-react-official";
import React from "react";
import { useIntl } from "react-intl";

import { themeSlice } from "@tdp/common/theme";
import { useSelector } from "@tdp/core";
import { EmptyState } from '@tdp/modules/apps'

import { Alignment, containerStyle, getStyles, multiLineWidth } from "./Charts.Styles";
import { customToolTip, Point } from "./CustomToolTip";
import { AxisType, ChartType, IDateUsageDataPoint, Period, removeWatermark } from "./IAnalyticsCharts";
import { formatDateIntl24 } from "./utils";



// Initialize the Highcharts accessibility module
// https://www.highcharts.com/docs/accessibility/accessibility-module#describing-your-chart
Accessibility(Highcharts);

export const ChartAreaLine: React.FC<{ data?: IDateUsageDataPoint[]; legend: string; period: Period }> = React.memo(({ data, legend, period }) => {
  const intl = useIntl();
  const currentTheme = useSelector(themeSlice.selectors.getCurrentTheme);
  const style = getStyles(currentTheme);

  if (!data) {
    return <EmptyState />;
  }

  const chartOptions = {
    title: null,
    chart: {
      type: ChartType.AREA,
      backgroundColor: style.backgroundColor
    },
    yAxis: {
      title: null,
      min: 0,
      labels: {
        style: {
          color: style.color
        }
      }
    },
    xAxis: {
      type: AxisType.DateTime,
      labels: {
        style: {
          color: style.color
        }
      }
    },
    plotOptions: {
      area: {
        fillColor: {
          linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
          stops: [
            [0, style.lineColors[0]],

            [1, Highcharts.color(style.lineColors[0]).setOpacity(0).get("rgba")]
          ]
        },
        marker: {
          enabled: false
        },
        lineWidth: multiLineWidth
      }
    },
    colors: style.lineColors,
    series: [
      {
        type: ChartType.AREA,
        name: legend,
        data: data.map((item) => [new Date(formatDateIntl24(item.date, intl)).getTime(), item.usage])
      }
    ],
    tooltip: {
      ...style.tooltip,
      formatter() {
        return `<div>${customToolTip(this as unknown as Point, intl, period)}</div>`;
      }
    },
    legend: {
      itemStyle: {
        color: style.legend
      },
      align: Alignment.Left
    },
    ...removeWatermark
  };

  return <HighchartsReact containerProps={containerStyle} highcharts={Highcharts} options={chartOptions} />;
});
